<template>
    <PortalContainer>
        <div
            v-if="showProtectedShell"
            class="bg-primary relative flex min-h-screen flex-col items-center"
            :class="{ 'impersonation-shell-active': isViewingImpersonationSubject, 'h-screen': isDesktop }"
        >
            <div v-if="isViewingImpersonationSubject" class="impersonation-frame" aria-hidden="true" />
            <div
                v-if="isViewingImpersonationSubject && impersonationSessions && isDesktop"
                class="impersonation-banner"
            >
                <div class="gap-xs flex min-w-0 items-center">
                    <span class="impersonation-banner-icon">
                        <i class="i-user size-4" />
                    </span>
                    <p class="min-w-0 truncate text-sm">
                        Impersonating
                        <strong>{{ impersonationSessions.subject.label }}</strong>
                        <span class="text-(--text-muted)"> · signed in as {{ impersonationSessions.original.label }}</span>
                    </p>
                </div>
                <button class="impersonation-stop-button" type="button" @click="authStore.endImpersonation">
                    Stop impersonating
                </button>
            </div>
            <div
                class="bg-lightest lg:bg-primary w-full flex-1 auto-rows-auto align-top"
                :class="{
                    'main-grid' /*'grid grid-cols-[3fr_14fr] grid-rows-[74px_1fr]' */: isDesktop,
                }"
            >
                <div v-if="isDesktop" class="px-md py-sm flex w-full justify-start">
                    <RouterLink to="/" alt="home" aria-label="home" class="gap-xs flex w-full items-center">
                        <i class="i-logo aspect-[98/21] h-6 w-fit" />
                    </RouterLink>
                </div>

                <div class="flex w-full justify-end">
                    <HeaderBar v-if="isDesktop" />
                    <MobileHeaderBar v-else-if="scrollableDiv" :scroll-target="scrollableDiv!" />
                </div>

                <nav v-if="isDesktop" class="col-start-1 flex min-h-0 flex-col justify-between overflow-y-auto">
                    <span v-if="isLMSView"></span>
                    <SideMenuAdmin v-if="!isLMSView && isAdminView" />
                    <SideMenu v-if="!isAdminView && !isLMSView" />

                    <div class="p-sm gap-xs relative flex flex-col pb-6">
                        <button
                            v-if="showFeedbackButton"
                            name="feedback"
                            class="gap-xs router-link py-xs flex w-full items-center rounded-sm transition-colors hover:bg-surface-hover"
                            @click="openFeedbackModalDialog"
                        >
                            <i class="i-mail h-5 w-5 grayscale" />
                            <span class="font-bold">Feedback</span>
                        </button>

                        <button
                            name="contact info"
                            class="gap-xs router-link py-xs flex w-full items-center rounded-sm transition-colors hover:bg-surface-hover"
                            @click.prevent="toggleContactModal"
                        >
                            <i class="i-phone size-5" />
                            <span class="font-bold">Help</span>
                        </button>

                        <ContactModal
                            v-if="showContactModal"
                            class="bottom-xl absolute"
                            @close="toggleContactModal"
                            @open-help-feedback="openHelpFeedbackFromContact"
                        />
                    </div>
                </nav>

                <div
                    ref="scrollableDiv"
                    data-testid="routed-content"
                    class="bg-lightest col-start-2 row-span-2 flex h-full max-h-screen w-full flex-col overflow-y-auto pt-32 lg:max-h-[calc(100vh-74px)] lg:rounded-tl-md lg:pt-0"
                >
                    <div
                        v-if="isViewingImpersonationSubject && impersonationSessions && !isDesktop"
                        class="impersonation-mobile-card"
                    >
                        <div class="gap-xs flex min-w-0 items-start">
                            <span class="impersonation-banner-icon">
                                <i class="i-user size-4" />
                            </span>
                            <div class="min-w-0 flex-1">
                                <p class="text-xs font-semibold">Impersonating</p>
                                <p class="text-sm leading-5 font-semibold break-words">
                                    {{ impersonationSessions.subject.label }}
                                </p>
                                <p class="text-(--text-muted) mt-1 text-xs leading-4">
                                    signed in as {{ impersonationSessions.original.label }}
                                </p>
                            </div>
                        </div>
                        <button class="impersonation-stop-button" type="button" @click="authStore.endImpersonation">
                            Stop impersonating
                        </button>
                    </div>
                    <!--
                        Padded routes use flex-1, not h-full: an h-full section stays one viewport tall and long
                        pages overflow it, which strands pb-lg mid-page (ITDEVELOP-1919). flex-1 still fills the
                        scroll container on short pages but grows with the content, so pb-lg is the gap after
                        the last element on every page. Views should not add their own bottom padding.
                    -->
                    <section
                        class="w-full max-w-full lg:mx-auto lg:max-w-[2400px]"
                        :class="{
                            'relative grid grid-cols-12 items-start': isDesktop,
                            'px-xs lg:px-md pb-lg pt-sm lg:pt-xl flex-1': !isLMSView && !$route.meta.fullHeight,
                            'h-full': isLMSView || $route.meta.fullHeight,
                        }"
                    >
                        <RouterView :key="$route.path" />
                        <div class="fixed z-[100]">
                            <TransitionGroup
                                name="toast"
                                tag="div"
                                class="gap-xs fixed right-5 bottom-5 flex w-96 flex-col items-end"
                            >
                                <ToastMessage
                                    v-for="toast in toastMessages"
                                    :key="toast.index"
                                    :message="toast.message"
                                    :type="toast.type"
                                    :index="toast.index"
                                />
                                <ToastMessage
                                    v-for="toast in adminToastMessages"
                                    :key="toast.index"
                                    :message="toast.message"
                                    :type="toast.type"
                                    :index="toast.index"
                                    store="admin"
                                />
                                <ToastMessage
                                    v-for="toast in notificationToastMessages"
                                    :key="toast.index"
                                    :message="toast.message"
                                    :type="toast.type"
                                    :index="toast.index"
                                    store="notification"
                                />
                            </TransitionGroup>
                        </div>
                    </section>
                </div>
            </div>
            <FeedbackModal
                v-if="!isLMSView"
                v-show="openFeedbackModal"
                :show-modal="openFeedbackModal"
                @close="closeFeedbackModalDialog"
            />
            <HelpFeedbackModal
                v-if="!isLMSView"
                :show-modal="openHelpFeedbackModal"
                @close="closeHelpFeedbackModalDialog"
            />
        </div>
        <div v-else-if="isAuthenticated && !showNavBar" class="flex min-h-screen w-full overflow-y-auto bg-lightest">
            <RouterView :key="$route.fullPath" />
            <div class="fixed z-[100]">
                <TransitionGroup
                    name="toast"
                    tag="div"
                    class="gap-xs fixed right-5 bottom-5 flex w-96 flex-col items-end"
                >
                    <ToastMessage
                        v-for="toast in toastMessages"
                        :key="toast.index"
                        :message="toast.message"
                        :type="toast.type"
                        :index="toast.index"
                    />
                    <ToastMessage
                        v-for="toast in adminToastMessages"
                        :key="toast.index"
                        :message="toast.message"
                        :type="toast.type"
                        :index="toast.index"
                        store="admin"
                    />
                    <ToastMessage
                        v-for="toast in notificationToastMessages"
                        :key="toast.index"
                        :message="toast.message"
                        :type="toast.type"
                        :index="toast.index"
                        store="notification"
                    />
                </TransitionGroup>
            </div>
        </div>
        <div v-else class="i-login bg-primary flex h-full w-full items-center justify-center">
            <RouterView :key="$route.fullPath" />
            <div class="fixed z-[100]">
                <TransitionGroup
                    name="toast"
                    tag="div"
                    class="gap-xs fixed right-5 bottom-5 flex w-96 flex-col items-end"
                >
                    <ToastMessage
                        v-for="toast in toastMessages"
                        :key="toast.index"
                        :message="toast.message"
                        :type="toast.type"
                        :index="toast.index"
                    />
                </TransitionGroup>
            </div>
        </div>
        <DevMenu />
    </PortalContainer>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, provide } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useIsDesktop } from '@/composables/isDesktop';
import SideMenu from './components/side-menu.vue';
import HeaderBar from './components/header-bar.vue';
import MobileHeaderBar from './components/mobile/mobile-header.vue';
import ToastMessage from './components/toast-message.vue';
import { useOrganizationStore } from './stores/organization';
import SideMenuAdmin from './components/side-menu-admin.vue';
import { useAdminStore } from './stores/admin';
import { useNotificationStore } from './stores/notification';
import ContactModal from './components/misc/contact-modal.vue';
import DevMenu from './components/misc/dev-menu.vue';
import FeedbackModal from './views/feedback-modal.vue';
import HelpFeedbackModal from './views/help-feedback-modal.vue';
import { useAuthStore } from './stores/auth';
import { shouldShowFeedbackButton } from './app-sidebar-visibility';

import { useIsLMSView } from './composables/isLMSView';

import PortalContainer from './components/support/portal-container.vue';

localStorage.setItem('admin', localStorage.getItem('admin') || 'false');

const route = useRoute();
const authStore = useAuthStore();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const impersonationSessions = computed(() => authStore.impersonationSessions);
const isViewingImpersonationSubject = computed(() => impersonationSessions.value?.active === 'subject');
const isDesktop = useIsDesktop();

const toastMessages = computed(() => useOrganizationStore().toastMessage);
const adminToastMessages = computed(() => useAdminStore().toastMessage);
const notificationToastMessages = computed(() => useNotificationStore().toastMessage);
const openFeedbackModal = ref<boolean>(false);
const openHelpFeedbackModal = ref<boolean>(false);
const isAdminView = computed(() => JSON.parse(localStorage.getItem('admin') || 'false'));
const isLMSView = useIsLMSView();
const showFeedbackButton = computed(() =>
    shouldShowFeedbackButton({
        isAdminView: isAdminView.value,
        isLMSView: isLMSView.value,
    }),
);

const showNavBar = computed(() => route.matched.length > 0 && !route.matched.some((record) => record.meta.hideNavBar));
// During logout we clear auth state before the router finishes reaching `/login`.
// Keep the protected shell mounted until navigation lands on a hideNavBar route,
// otherwise admin mode briefly swaps from the app shell to the logged-out shell mid-transition.
// TODO: Remove this once logout becomes a normal SPA transition by replacing the delayed full-page reload
// with explicit store reset/navigation sequencing that leaves the router and top-level shell in sync.
const showProtectedShell = computed(() => showNavBar.value && (isAuthenticated.value || authStore.logoutInProgress));
const showContactModal = ref<boolean>();

function toggleContactModal() {
    showContactModal.value = !showContactModal.value;
}

function openFeedbackModalDialog() {
    openFeedbackModal.value = true;
}

function closeFeedbackModalDialog() {
    openFeedbackModal.value = false;
}

function closeHelpFeedbackModalDialog() {
    openHelpFeedbackModal.value = false;
}

function openHelpFeedbackFromContact() {
    showContactModal.value = false;
    openHelpFeedbackModal.value = true;
}

// to be able to scroll to top, from anywhere
const scrollableDiv = ref<HTMLDivElement | null>(null);
const scrollToTop = () => {
    console.log('scrolling To Top!');
    if (scrollableDiv.value) {
        scrollableDiv.value.scrollTo({ top: 0, behavior: 'smooth' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
};
provide('scrollToTop', scrollToTop);

onMounted(() => {
});

onUnmounted(() => {
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';

.main-grid {
    --desktop-sidebar-width: 272px;
    display: grid;
    grid-template-columns: minmax(auto, var(--desktop-sidebar-width)) 1fr;
    /* The shell is pinned to the viewport on desktop: a side menu taller than
       the window scrolls inside its own column instead of growing the row and
       scrolling the whole page out from under the routed content. */
    grid-template-rows: 74px minmax(0, 1fr);
    min-height: 0;
}

.impersonation-shell-active {
    --impersonation-frame-width: 3px;
    --impersonation-banner-height: 55px;
    --impersonation-warning-light: var(--color-orange-50);
    --impersonation-warning-dark: rgb(234 115 23);
    box-sizing: border-box;
    padding-top: calc(var(--impersonation-frame-width) + var(--impersonation-banner-height));
}

.impersonation-frame {
    position: fixed;
    inset: 0;
    z-index: 92;
    pointer-events: none;
    border: 3px solid var(--impersonation-warning-dark);
}

.impersonation-banner {
    position: fixed;
    top: var(--impersonation-frame-width);
    right: var(--impersonation-frame-width);
    left: var(--impersonation-frame-width);
    z-index: 91;
    display: flex;
    min-height: 44px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid var(--impersonation-warning-light);
    background: var(--impersonation-warning-light);
    padding: 8px 50px;
}

.impersonation-banner-icon {
    display: flex;
    height: 20px;
    width: 20px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: var(--impersonation-warning-dark);
    color: white;
}

/* ITDEVELOP-3090: no filter needed — .i-user is a currentColor mask now and
   inherits the banner's white text colour. */

.impersonation-stop-button {
    /* Secondary-button tokens — charcoal again since ITDEVELOP-3106 reversed
       the 2723 light-surface cut, restoring this button's deployed look on
       the impersonation banner. */
    background-color: var(--btn-secondary-bg);
    color: var(--btn-secondary-fg);
    flex: 0 0 auto;
    border-radius: 6px;
    padding: 7px 14px;
    font-size: 13px;
    font-weight: 700;
    transition: background-color 0.3s ease;
}

.impersonation-stop-button:hover {
    background-color: var(--btn-secondary-hover-bg);
}

@media (max-width: 1023px) {
    .impersonation-shell-active {
        --impersonation-frame-width: 2px;
        --impersonation-banner-height: 0px;
        padding-top: 0;
    }

    .impersonation-frame {
        border-width: 2px;
    }

    .impersonation-mobile-card {
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        margin: 0 var(--spacing-xs) var(--spacing-lg);
        border: 1px solid var(--impersonation-warning-dark);
        border-radius: 8px;
        background: var(--impersonation-warning-light);
        padding: 12px;
    }

    .impersonation-mobile-card .impersonation-stop-button {
        width: 100%;
        padding: 9px 14px;
    }
}
</style>
